import { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, X, AlertCircle, Pencil, Check } from 'lucide-react';
import { vendorCostAnalysisApi } from '../../services/vendorCostAnalysisApi';
import { SearchableSelect } from '../ui/SearchableSelect';
import type { VCACategoryDetail, VCAUnmappedAccount, VCAAccountWithMappings } from '../../types/vendorCostAnalysis';

interface Props {
    canEdit: boolean;
}

export function CategorySettingsTab({ canEdit }: Props) {
    const [categories, setCategories] = useState<VCACategoryDetail[]>([]);
    const [unmappedAccounts, setUnmappedAccounts] = useState<VCAUnmappedAccount[]>([]);
    const [allAccounts, setAllAccounts] = useState<VCAAccountWithMappings[]>([]);
    const [mappedAccounts, setMappedAccounts] = useState<VCAUnmappedAccount[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedCategory, setSelectedCategory] = useState<VCACategoryDetail | null>(null);
    const [mappedLoading, setMappedLoading] = useState(false);
    const [addAccountID, setAddAccountID] = useState('');
    const [saving, setSaving] = useState(false);
    const [showAddCategory, setShowAddCategory] = useState(false);
    const [newCatCode, setNewCatCode] = useState('');
    const [newCatName, setNewCatName] = useState('');
    const [newCatDesc, setNewCatDesc] = useState('');
    const [error, setError] = useState('');
    const [successMsg, setSuccessMsg] = useState('');
    const [editingName, setEditingName] = useState(false);
    const [editName, setEditName] = useState('');
    const [editDesc, setEditDesc] = useState('');

    const loadCategories = useCallback(async () => {
        setLoading(true);
        try {
            // includeUnmapped powers the warning at the bottom of the category list,
            // mode=all-accounts powers the M:N picker (lets us add an already-mapped account
            // to a second category).
            const [base, all] = await Promise.all([
                vendorCostAnalysisApi.getCategories({ includeUnmapped: true }),
                vendorCostAnalysisApi.getCategories({ mode: 'all-accounts' }),
            ]);
            setCategories(base.categories);
            setUnmappedAccounts(base.unmappedAccounts);
            setAllAccounts(all.allAccounts);
        } catch { /* ignore */ }
        setLoading(false);
    }, []);

    useEffect(() => { loadCategories(); }, [loadCategories]);

    // Load mapped accounts when category selected
    const loadMappedAccounts = useCallback(async (categoryCode: string) => {
        setMappedLoading(true);
        try {
            const data = await vendorCostAnalysisApi.getCategories({ categoryCode });
            setMappedAccounts(data.mappedAccounts);
        } catch { setMappedAccounts([]); }
        setMappedLoading(false);
    }, []);

    useEffect(() => {
        if (selectedCategory) {
            loadMappedAccounts(selectedCategory.categoryCode);
        } else {
            setMappedAccounts([]);
        }
    }, [selectedCategory, loadMappedAccounts]);

    const handleAddCategory = async () => {
        if (!newCatCode.trim() || !newCatName.trim()) return;
        setSaving(true);
        setError('');
        try {
            await vendorCostAnalysisApi.saveCategory({
                categoryCode: newCatCode.trim(),
                categoryName: newCatName.trim(),
                categoryDescription: newCatDesc.trim() || undefined,
            });
            setShowAddCategory(false);
            setNewCatCode('');
            setNewCatName('');
            setNewCatDesc('');
            await loadCategories();
            setSuccessMsg('Category added');
            setTimeout(() => setSuccessMsg(''), 3000);
        } catch (err) {
            setError((err as Error).message);
        }
        setSaving(false);
    };

    const handleDeleteCategory = async (code: string) => {
        setSaving(true);
        setError('');
        try {
            await vendorCostAnalysisApi.deleteCategory(code);
            if (selectedCategory?.categoryCode === code) setSelectedCategory(null);
            await loadCategories();
            setSuccessMsg('Category deleted');
            setTimeout(() => setSuccessMsg(''), 3000);
        } catch (err) {
            setError((err as Error).message);
        }
        setSaving(false);
    };

    const handleAddMapping = async () => {
        if (!addAccountID || !selectedCategory) return;
        setSaving(true);
        setError('');
        try {
            await vendorCostAnalysisApi.saveAccountMapping({
                accountID: addAccountID,
                categoryCode: selectedCategory.categoryCode,
                action: 'add',
            });
            setAddAccountID('');
            await loadCategories();
            await loadMappedAccounts(selectedCategory.categoryCode);
            setSuccessMsg('Account mapped');
            setTimeout(() => setSuccessMsg(''), 3000);
        } catch (err) {
            setError((err as Error).message);
        }
        setSaving(false);
    };

    const handleRemoveMapping = async (accountID: string) => {
        if (!selectedCategory) return;
        setSaving(true);
        try {
            await vendorCostAnalysisApi.saveAccountMapping({
                accountID,
                categoryCode: selectedCategory.categoryCode,
                action: 'remove',
            });
            await loadCategories();
            await loadMappedAccounts(selectedCategory.categoryCode);
            setSuccessMsg('Account unmapped');
            setTimeout(() => setSuccessMsg(''), 3000);
        } catch { /* ignore */ }
        setSaving(false);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
        );
    }

    // Picker for the right-hand "Add Account to Category" control:
    // shows ALL P&L accounts (M:N) — already-mapped accounts get a "(in N other categories)"
    // hint. Accounts already mapped to the currently selected category are filtered out
    // (no point listing them — they're already in the mapped list below).
    const mappedAccountIDs = new Set(mappedAccounts.map(m => m.accountID));
    const accountPickerOptions = allAccounts
        .filter(a => !mappedAccountIDs.has(a.accountID))
        .map(a => {
            const base = `${a.accountNr} - ${a.accountName} (${a.companyCode})`;
            const hint = a.existingMappingCount > 0
                ? ` — already in: ${a.existingCategoryNames}`
                : '';
            return { id: a.accountID, name: base + hint };
        });

    return (
        <div className="flex gap-4" style={{ minHeight: '500px' }}>
            {/* Left Panel: Category List */}
            <div className="w-1/2 flex flex-col border-r pr-4">
                <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-medium text-gray-700">Cost Categories ({categories.length})</h4>
                    {canEdit && (
                        <button
                            onClick={() => setShowAddCategory(true)}
                            className="flex items-center gap-1 px-2 py-1 text-xs text-blue-600 hover:bg-blue-50 rounded"
                        >
                            <Plus className="w-3.5 h-3.5" /> Add Category
                        </button>
                    )}
                </div>

                {successMsg && (
                    <div className="bg-green-50 border border-green-200 text-green-700 px-3 py-1.5 rounded-md text-xs mb-2">{successMsg}</div>
                )}
                {error && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-1.5 rounded-md text-xs mb-2 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" /> {error}
                        <button onClick={() => setError('')} className="ml-auto"><X className="w-3 h-3" /></button>
                    </div>
                )}

                {showAddCategory && (
                    <div className="mb-3 p-3 border rounded-lg bg-gray-50 space-y-2">
                        <input type="text" placeholder="Code (e.g. Logistics)" value={newCatCode} onChange={e => setNewCatCode(e.target.value)} className="w-full border rounded px-2 py-1 text-sm" />
                        <input type="text" placeholder="Name" value={newCatName} onChange={e => setNewCatName(e.target.value)} className="w-full border rounded px-2 py-1 text-sm" />
                        <input type="text" placeholder="Description (optional)" value={newCatDesc} onChange={e => setNewCatDesc(e.target.value)} className="w-full border rounded px-2 py-1 text-sm" />
                        <div className="flex gap-2">
                            <button onClick={handleAddCategory} disabled={saving || !newCatCode.trim() || !newCatName.trim()} className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">
                                {saving ? 'Saving...' : 'Add'}
                            </button>
                            <button onClick={() => setShowAddCategory(false)} className="px-3 py-1 text-xs text-gray-500 border rounded hover:bg-gray-100">Cancel</button>
                        </div>
                    </div>
                )}

                <div className="flex-1 overflow-auto space-y-1">
                    {categories.map(c => (
                        <div
                            key={c.categoryCode}
                            onClick={() => setSelectedCategory(c)}
                            className={`p-3 rounded-lg cursor-pointer border transition-colors ${
                                selectedCategory?.categoryCode === c.categoryCode
                                    ? 'border-blue-300 bg-blue-50'
                                    : 'border-transparent hover:bg-gray-50'
                            }`}
                        >
                            <div className="flex items-center justify-between">
                                <div>
                                    <span className="font-medium text-sm">{c.categoryName}</span>
                                    <span className="text-xs text-gray-400 ml-1">({c.categoryCode})</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="text-xs text-gray-500">{c.accountCount} accounts</span>
                                    {canEdit && c.accountCount === 0 && (
                                        <button
                                            onClick={e => { e.stopPropagation(); handleDeleteCategory(c.categoryCode); }}
                                            className="p-1 text-gray-400 hover:text-red-500 rounded"
                                            title="Delete empty category"
                                        >
                                            <Trash2 className="w-3 h-3" />
                                        </button>
                                    )}
                                </div>
                            </div>
                            {c.categoryDescription && (
                                <div className="text-xs text-gray-400 mt-0.5">{c.categoryDescription}</div>
                            )}
                        </div>
                    ))}
                </div>

                {unmappedAccounts.length > 0 && (
                    <div className="mt-3 pt-3 border-t text-xs text-amber-600 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {unmappedAccounts.length} GL accounts with transactions are unmapped
                    </div>
                )}
            </div>

            {/* Right Panel: Mapped Accounts + Add */}
            <div className="w-1/2 pl-2">
                {!selectedCategory ? (
                    <div className="flex items-center justify-center h-full text-gray-400 text-sm">
                        Select a category to manage account mappings
                    </div>
                ) : (
                    <div className="space-y-4">
                        <div>
                            {editingName && canEdit ? (
                                <div className="space-y-2">
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="text"
                                            value={editName}
                                            onChange={e => setEditName(e.target.value)}
                                            className="text-lg font-semibold border rounded-md px-2 py-1 flex-1"
                                            autoFocus
                                            onKeyDown={e => {
                                                if (e.key === 'Enter') {
                                                    vendorCostAnalysisApi.saveCategory({
                                                        categoryCode: selectedCategory.categoryCode,
                                                        categoryName: editName.trim(),
                                                        categoryDescription: editDesc.trim() || undefined,
                                                    }).then(() => {
                                                        setEditingName(false);
                                                        loadCategories();
                                                        setSelectedCategory(prev => prev ? { ...prev, categoryName: editName.trim(), categoryDescription: editDesc.trim() } : null);
                                                    });
                                                }
                                                if (e.key === 'Escape') setEditingName(false);
                                            }}
                                        />
                                        <button
                                            onClick={() => {
                                                vendorCostAnalysisApi.saveCategory({
                                                    categoryCode: selectedCategory.categoryCode,
                                                    categoryName: editName.trim(),
                                                    categoryDescription: editDesc.trim() || undefined,
                                                }).then(() => {
                                                    setEditingName(false);
                                                    loadCategories();
                                                    setSelectedCategory(prev => prev ? { ...prev, categoryName: editName.trim(), categoryDescription: editDesc.trim() } : null);
                                                });
                                            }}
                                            className="p-1 text-green-600 hover:bg-green-50 rounded"
                                        >
                                            <Check className="w-4 h-4" />
                                        </button>
                                        <button onClick={() => setEditingName(false)} className="p-1 text-gray-400 hover:bg-gray-100 rounded">
                                            <X className="w-4 h-4" />
                                        </button>
                                    </div>
                                    <input
                                        type="text"
                                        value={editDesc}
                                        onChange={e => setEditDesc(e.target.value)}
                                        placeholder="Description (optional)"
                                        className="w-full border rounded px-2 py-1 text-sm text-gray-500"
                                    />
                                </div>
                            ) : (
                                <div className="flex items-center gap-2">
                                    <h3 className="text-lg font-semibold">{selectedCategory.categoryName}</h3>
                                    {canEdit && (
                                        <button
                                            onClick={() => { setEditName(selectedCategory.categoryName); setEditDesc(selectedCategory.categoryDescription); setEditingName(true); }}
                                            className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                                            title="Rename category"
                                        >
                                            <Pencil className="w-3.5 h-3.5" />
                                        </button>
                                    )}
                                </div>
                            )}
                            {!editingName && selectedCategory.categoryDescription && (
                                <p className="text-sm text-gray-500 mt-0.5">{selectedCategory.categoryDescription}</p>
                            )}
                        </div>

                        {/* Add account to this category (M:N — same account may be in multiple categories) */}
                        {canEdit && accountPickerOptions.length > 0 && (
                            <div>
                                <h4 className="text-sm font-medium text-gray-700 mb-2">Add Account to Category</h4>
                                <div className="flex gap-2">
                                    <SearchableSelect
                                        options={accountPickerOptions}
                                        value={addAccountID}
                                        onChange={setAddAccountID}
                                        placeholder="Search accounts..."
                                        className="flex-1"
                                    />
                                    <button
                                        onClick={handleAddMapping}
                                        disabled={saving || !addAccountID}
                                        className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                                    >
                                        <Plus className="w-4 h-4" />
                                    </button>
                                </div>
                                <p className="text-xs text-gray-400 mt-1">
                                    An account may be mapped to multiple categories — e.g. add the same
                                    freight account to both a specific category and an aggregate one.
                                </p>
                            </div>
                        )}

                        {/* Mapped Accounts List */}
                        <div>
                            <h4 className="text-sm font-medium text-gray-700 mb-2">
                                Mapped Accounts ({mappedAccounts.length})
                            </h4>
                            {mappedLoading ? (
                                <div className="flex items-center justify-center h-16">
                                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600" />
                                </div>
                            ) : (
                                <div className="max-h-[400px] overflow-auto">
                                    <table className="w-full text-sm">
                                        <thead className="sticky top-0 bg-white">
                                            <tr className="text-left text-xs font-medium text-gray-500 uppercase border-b">
                                                <th className="py-1.5 pr-2">Company</th>
                                                <th className="py-1.5 pr-2">Account Nr</th>
                                                <th className="py-1.5 pr-2">Account Name</th>
                                                {canEdit && <th className="py-1.5 w-8"></th>}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {mappedAccounts.map(a => (
                                                <tr key={a.accountID} className="border-b border-gray-100 hover:bg-gray-50">
                                                    <td className="py-1.5 pr-2 text-xs font-mono">{a.companyCode}</td>
                                                    <td className="py-1.5 pr-2 text-xs font-mono">{a.accountNr}</td>
                                                    <td className="py-1.5 pr-2 text-xs">{a.accountName}</td>
                                                    {canEdit && (
                                                        <td className="py-1.5">
                                                            <button
                                                                onClick={() => handleRemoveMapping(a.accountID)}
                                                                className="p-0.5 text-gray-300 hover:text-red-500 rounded"
                                                                title="Remove mapping"
                                                            >
                                                                <X className="w-3.5 h-3.5" />
                                                            </button>
                                                        </td>
                                                    )}
                                                </tr>
                                            ))}
                                            {mappedAccounts.length === 0 && (
                                                <tr>
                                                    <td colSpan={canEdit ? 4 : 3} className="py-4 text-center text-gray-400 text-xs">
                                                        No accounts mapped to this category
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>

                        <div className="bg-blue-50 border border-blue-200 rounded-md px-3 py-2 text-xs text-blue-700">
                            Categories and account mappings are applied immediately. They are not
                            managed by ADF and persist across data refreshes.
                            <br />
                            <strong>Accounts can belong to multiple categories</strong> — e.g. create a
                            "Freight Total" category that aggregates all freight accounts in addition
                            to keeping separate "Freight Purchase" and "Freight Sales" categories.
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
